import { PageHeader } from 'app/components/ui/page-header';
import { Button, Card, CardDescription, CardFooter, CardHeader, CardTitle } from '@hqo/ui-atoms';

import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { useCallback, useMemo } from 'react';
import { useUser } from 'app/shared/contexts/user';
import { useErrors } from 'app/shared/contexts/errors';

type SectionVariant = 'default' | 'outline' | 'destructive';

interface Section {
  key: string;
  testId: string;
  variant: SectionVariant;
  onClick: () => void;
  /** Extra button classes — e.g. a hover contrast guard for the outline variant. */
  className?: string;
}

export const HelloReactPage = (): JSX.Element => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { user } = useUser();
  const { addError } = useErrors();
  const name = [user?.first_name, user?.last_name].filter(Boolean).join(' ');

  const handleNavigateToSDKDemo = useCallback(() => {
    navigate('/sdk-demo');
  }, [navigate]);

  const handleNavigateToError = useCallback(() => {
    navigate('/error');
  }, [navigate]);

  const handleShowErrorModal = useCallback(() => {
    addError('demo-error', {
      error: new Error('This is a demo error modal triggered from the table of contents.'),
      errorCode: 'demo',
    });
  }, [addError]);

  const sections = useMemo<Section[]>(
    () => [
      { key: 'sdkDemo', testId: 'navigate-sdk-demo', variant: 'default', onClick: handleNavigateToSDKDemo },
      // outline hover bg is the (often dark) brand --accent; force light text so it stays ADA-legible
      { key: 'errorPage', testId: 'navigate-error', variant: 'outline', onClick: handleNavigateToError, className: 'hover:text-white' },
      { key: 'errorModal', testId: 'show-error-modal', variant: 'destructive', onClick: handleShowErrorModal },
    ],
    [handleNavigateToSDKDemo, handleNavigateToError, handleShowErrorModal],
  );

  return (
    <div data-testid="hello-react">
      <PageHeader
        title={intl.formatMessage({ id: 'welcomeMessage' }, { name })}
        className="mb-4 border-b border-system-grey-medium pb-4"
      />
      <div className="flex flex-col gap-6 px-4">
        <div className="flex flex-col gap-1">
          <h2 className="text-xl font-medium">
            <FormattedMessage id="tableOfContents.title" />
          </h2>
          <p className="text-sm text-muted-foreground">
            <FormattedMessage id="tableOfContents.description" />
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {sections.map(({ key, testId, variant, onClick, className }) => (
            <Card key={key} className="flex flex-col justify-between">
              <CardHeader>
                <CardTitle>
                  <FormattedMessage id={`tableOfContents.${key}.title`} />
                </CardTitle>
                <CardDescription>
                  <FormattedMessage id={`tableOfContents.${key}.description`} />
                </CardDescription>
              </CardHeader>
              <CardFooter>
                <Button onClick={onClick} variant={variant} size="sm" data-testid={testId} className={className}>
                  <FormattedMessage id={`tableOfContents.${key}.buttonText`} />
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};
